const sanitizeText = (value: any, maxLength: number = 180) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);

const slugify = (value: string, fallback: string) => {
  const normalized = sanitizeText(value, 80)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalized || fallback;
};

const getElementLabel = (element: HTMLElement) =>
  sanitizeText(
    element.getAttribute("data-telemetry-label")
    || element.getAttribute("aria-label")
    || element.getAttribute("title")
    || ("value" in element ? (element as HTMLInputElement).value : "")
    || element.textContent
    || element.id
    || element.getAttribute("name")
    || "",
  );

export const extractClickTelemetry = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  const clickableElement = target.closest(
    "[data-telemetry-action], button, [role='button'], [role='menuitem'], .p-button, a[href], .p-menuitem-link, input[type='submit']",
  ) as HTMLElement | null;

  if (!clickableElement) {
    return null;
  }

  const label = getElementLabel(clickableElement);
  const tagName = String(clickableElement.tagName || "unknown").toLowerCase();
  const explicitAction = sanitizeText(clickableElement.getAttribute("data-telemetry-action"), 100);
  const action = explicitAction || `click_${tagName}_${slugify(label, "unnamed")}`;

  return {
    metricType: "ui_click",
    action,
    label: label || "Unnamed action",
    page: window.location.pathname,
    metadata: {
      tagName,
      id: sanitizeText(clickableElement.id, 80) || null,
      role: sanitizeText(clickableElement.getAttribute("role"), 40) || null,
      href: sanitizeText(clickableElement.getAttribute("href"), 180) || null,
      text: label || null,
    },
  };
};

export const buildPageViewTelemetry = (pathname: string) => ({
  metricType: "page_view",
  page: pathname,
  action: `page_view_${slugify(pathname, "root")}`,
});

export const buildJsErrorTelemetry = ({
  errorType,
  message,
  stack,
  source,
  page,
}: {
  errorType: string;
  message?: string;
  stack?: string;
  source?: string;
  page?: string;
}) => ({
  metricType: "js_error",
  page: page || window.location.pathname,
  action: `js_error_${slugify(errorType, "unknown")}`,
  errorType: sanitizeText(errorType, 80),
  message: sanitizeText(message, 500),
  metadata: {
    source: sanitizeText(source, 200) || null,
    stack: sanitizeText(stack, 1000) || null,
  },
});

